import { Moon, Sun } from "lucide-react";
import { useAppearance, type Style } from "../lib/theme";
import ProfileSettings from "../components/ProfileSettings";
import PasskeySettings from "../components/PasskeySettings";

const STYLES: { key: Style; name: string; desc: string }[] = [
  { key: "standard", name: "Standard", desc: "Clean flat surfaces with subtle borders — the default." },
  { key: "neo", name: "Neumorphic", desc: "Soft extruded surfaces with sculpted shadows." },
  { key: "glass", name: "Glass", desc: "Translucent frosted panels over an ambient gradient." },
];

/** Mini preview rendered with hardcoded style-specific classes so each card
 * shows its own look regardless of the active theme. */
function Preview({ style, dark }: { style: Style; dark: boolean }) {
  const base = "h-24 rounded-xl p-3 flex flex-col justify-between transition-all";
  if (style === "glass") {
    return (
      <div className={`${base} relative overflow-hidden ${dark ? "bg-slate-900" : "bg-slate-100"}`}>
        <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-teal-400/40 blur-xl" />
        <div className="absolute -bottom-8 -right-4 h-24 w-24 rounded-full bg-indigo-400/40 blur-xl" />
        <div className={`relative rounded-lg border px-2 py-1.5 backdrop-blur ${dark ? "bg-white/10 border-white/15" : "bg-white/50 border-white/60"}`}>
          <div className={`h-1.5 w-12 rounded ${dark ? "bg-white/50" : "bg-slate-500/50"}`} />
          <div className={`mt-1 h-1.5 w-8 rounded ${dark ? "bg-white/25" : "bg-slate-400/40"}`} />
        </div>
        <div className="relative h-4 w-14 rounded bg-teal-600/90" />
      </div>
    );
  }
  if (style === "neo") {
    return (
      <div className={`${base} ${dark ? "bg-[#181d2b]" : "bg-[#e3e8f0]"}`}>
        <div className={`rounded-lg px-2 py-1.5 ${dark
          ? "bg-[#181d2b] shadow-[3px_3px_6px_#10141e,-3px_-3px_6px_#202638]"
          : "bg-[#e3e8f0] shadow-[3px_3px_6px_#c3cad6,-3px_-3px_6px_#ffffff]"}`}>
          <div className={`h-1.5 w-12 rounded ${dark ? "bg-slate-500" : "bg-slate-400"}`} />
          <div className={`mt-1 h-1.5 w-8 rounded ${dark ? "bg-slate-600" : "bg-slate-300"}`} />
        </div>
        <div className={`h-4 w-14 rounded bg-teal-700 ${dark
          ? "shadow-[2px_2px_4px_#10141e]" : "shadow-[2px_2px_4px_#c3cad6]"}`} />
      </div>
    );
  }
  return (
    <div className={`${base} ${dark ? "bg-slate-900" : "bg-slate-100"}`}>
      <div className={`rounded-lg border px-2 py-1.5 ${dark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
        <div className={`h-1.5 w-12 rounded ${dark ? "bg-slate-500" : "bg-slate-400"}`} />
        <div className={`mt-1 h-1.5 w-8 rounded ${dark ? "bg-slate-600" : "bg-slate-300"}`} />
      </div>
      <div className="h-4 w-14 rounded bg-teal-700" />
    </div>
  );
}

export default function Settings() {
  const { theme, style, setStyle, toggleTheme } = useAppearance();
  const dark = theme === "dark";

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-ink">Settings</h1>
        <p className="text-sm text-mut">Appearance preferences are saved on this device.</p>
      </div>

      <ProfileSettings />

      <PasskeySettings />

      <section className="space-y-3">
        <h2 className="text-lg font-medium text-ink">Mode</h2>
        <div className="flex gap-3">
          {(["light", "dark"] as const).map((m) => (
            <button
              key={m}
              onClick={() => theme !== m && toggleTheme()}
              className={`card flex items-center gap-3 px-5 py-4 transition-all ${
                theme === m ? "ring-2 ring-teal-500" : "opacity-70 hover:opacity-100"
              }`}
            >
              {m === "light" ? <Sun className="h-5 w-5 text-amber-500" /> : <Moon className="h-5 w-5 text-indigo-400" />}
              <span className="text-sm font-medium text-ink capitalize">{m}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-medium text-ink">Visual style</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {STYLES.map((s) => (
            <button
              key={s.key}
              onClick={() => setStyle(s.key)}
              className={`card p-4 text-left transition-all ${
                style === s.key ? "ring-2 ring-teal-500" : "opacity-80 hover:opacity-100"
              }`}
            >
              <Preview style={s.key} dark={dark} />
              <p className="mt-3 text-sm font-medium text-ink">{s.name}
                {style === s.key && <span className="ml-2 chip bg-teal-50 text-teal-800">active</span>}
              </p>
              <p className="mt-0.5 text-xs text-mut">{s.desc}</p>
            </button>
          ))}
        </div>
      </section>

      <p className="text-xs text-faint">Organisation-wide branding (logo, colors) is configured by owners under org settings — a later slice (FR-TEN-05).</p>
    </div>
  );
}
